import { css, CSSResult } from 'lit'
import {
  csColorAction,
  csColorBackground,
  csColorBorder,
  csColorText
} from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csBorder } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'

export const cseSwatch = {
  borderColor: css`var(--cse-swatch-border-color, ${csColorBorder.light})`,
  borderColorHover: css`var(--cse-swatch-border-color-hover, ${csColorAction.secondaryHover})`,
  borderWidthHover: css`var(--cse-swatch-border-width-hover, ${csBorder.width})`,
  borderWidth: css`var(--cse-swatch-border-width, ${csBorder.width})`,
  color: css`var(--cse-swatch-color, ${csColorText.body})`,
  sizeSmall: css`var(--cse-swatch-small-size, 29px)`,
  sizeLarge: css`var(--cse-swatch-large-size, 60px)`,
  afterBackgroundColor: css`var(--cse-swatch-after-background, ${csColorBorder.light})`,
  backgroundColorOutOfStock: css`var(--cse-swatch-background-color-out-of-stock, ${csColorBackground.disabled})`,
  afterBackgroundColorOutOfStock: css`var(--cse-swatch-after-background-color-out-of-stock, ${csColorBorder.medium})`,
  borderColorOutOfStock: css`var(--cse-swatch-border-color-out-of-stock, ${csColorBorder.light})`,
  opacityOutOfStock: css`var(--cse-swatch-opacity-out-of-stock, 0.65)`,
}

export const cseSwatchDeprecated = {
  sizeSmall: css`var(--cse-swatch-size-small, ${cseSwatch.sizeSmall})`,
  sizeLarge: css`var(--cse-swatch-size-large, ${cseSwatch.sizeLarge})`,
}

export const cseTextSwatch = {
  backgroundColor: css`var(--cse-text-swatch-background-color, ${csColorBackground.primary})`,
  paddingInlineSmall: css`var(--cse-text-swatch-small-padding-x, ${csSpacing.spacing05})`,
  paddingInlineLarge: css`var(--cse-text-swatch-large-padding-x, 25px)`,
  borderRadius: css`var(--cse-text-swatch-border-radius, ${csBorder.radiusMinimal})`,
}

export const cseImageSwatch = {
  backgroundColor: css`var(--cse-image-swatch-background-color, ${csColorBackground.secondary})`,
  borderRadius: css`var(--cse-image-swatch-border-radius, ${csBorder.radiusMinimal})`,
}

export const cseColorSwatch = {
  borderRadius: css`var(--cse-color-swatch-border-radius, ${csBorder.radiusFull})`,
  outlineColor: css`var(--cse-color-swatch-outline-color, ${csColorAction.secondary})`,
  sizeSmall: css`var(--cse-color-swatch-small-size, ${csSpacing.spacing06})`,
  sizeLarge: css`var(--cse-color-swatch-large-size, 25px)`,
}

export const cseColorSwatchDeprecated = {
  sizeSmall: css`var(--cse-color-swatch-size-small, ${cseColorSwatch.sizeSmall})`,
  sizeLarge: css`var(--cse-color-swatch-size-large, ${cseColorSwatch.sizeLarge})`,
  backgroundColorDisabled: css`var(--cse-swatch-background-color-disabled, ${csColorBackground.disabled})`,
  borderColorDisabled: css`var(--cse-swatch-border-color-disabled, ${csColorBorder.light})`,
  opacityDisabled: css`var(--cse-swatch-opacity-disabled, 0.65)`
}

export const style: CSSResult = css`
  .cse-swatch {
    border-width: ${cseSwatch.borderWidth};
    border-style: solid;
    border-color: ${cseSwatch.borderColor};
    display: inline-block;
  }

  .cse-swatch:hover {
    cursor: pointer;
  }

  .cse-swatch.out-of-stock {
    position: relative;
    border-width: ${cseSwatch.borderWidth};
    border-style: solid;
    border-color: ${cseSwatch.borderColorOutOfStock};
    background-color: ${cseSwatch.backgroundColorOutOfStock};
    overflow: hidden;
  }

  .cse-swatch.out-of-stock::after {
    background-color: ${cseSwatch.afterBackgroundColorOutOfStock};
    content: "";
    height: 2px;
    inset: 50%;
    position: absolute;
    transform: translate(-50%, -50%) rotate(45deg);
    width: 150%;
  }

  .cse-swatch.disabled {
    position: relative;
    opacity: ${cseColorSwatchDeprecated.opacityDisabled};
    border-width: ${cseSwatch.borderWidth};
    border-style: solid;
    border-color: ${cseColorSwatchDeprecated.borderColorDisabled};
    background-color: ${cseSwatch.backgroundColorOutOfStock};
    overflow: hidden;
  }

  .cse-swatch.disabled::after {
    background-color: ${cseSwatch.afterBackgroundColorOutOfStock};
    content: "";
    height: 2px;
    inset: 50%;
    position: absolute;
    transform: translate(-50%, -50%) rotate(45deg);
    width: 150%;
  }

  .cse-swatch.disabled:hover {
    cursor: not-allowed;
  }

  .cse-swatch.selected:hover {
    cursor: default;
  }

  /* Image and text */
  .variant-image,
  .variant-text {
    --cse-text-color: ${cseSwatch.color};
  }

  .variant-text {
    background-color: ${cseTextSwatch.backgroundColor};
    border-radius: ${cseTextSwatch.borderRadius};
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .variant-image {
    --cse-image-height: 100%;
    /* The image always fills the swatch: shield it from a theme's inherited aspect ratio */
    --cse-image-aspect-ratio: initial;
    background-color: ${cseImageSwatch.backgroundColor};
    border-radius: ${cseImageSwatch.borderRadius};
    overflow: hidden;
    padding: 0;
    position: relative;
  }

  .variant-image.out-of-stock {
    opacity: ${cseSwatch.opacityOutOfStock};
  }

  .variant-image.selected,
  .variant-text.selected,
  .variant-image:not(.disabled):hover,
  .variant-text:not(.disabled):hover {
    border-color: ${cseSwatch.borderColorHover};
    border-width: ${cseSwatch.borderWidthHover};
  }

  .variant-image.out-of-stock::before,
  .variant-image.disabled::before {
    background-color: rgba(0, 0, 0, 0.3);
    content: "";
    inset: 0;
    position: absolute;
  }

  .variant-image.out-of-stock::after,
  .variant-image.disabled::after {
    background-color: ${cseSwatch.afterBackgroundColor};
  }

  /* Color variant */
  .variant-color {
    position: relative;
    border-radius: ${cseColorSwatch.borderRadius};
  }

  .variant-color.selected,
  .variant-color:not(.disabled):hover {
    outline-width: ${cseSwatch.borderWidth};
    outline-style: solid;
    outline-color: ${cseColorSwatch.outlineColor};
    outline-offset: ${cseSwatch.borderWidth};
  }

  .variant-color.out-of-stock::after,
  .variant-color.disabled::after {
    background-color: ${cseSwatch.borderColor};
    height: ${cseSwatch.borderWidth};
  }

  /* Sizes */
  .size-small {
    width: ${cseSwatchDeprecated.sizeSmall};
    height: ${cseSwatchDeprecated.sizeSmall};
  }

  .size-large {
    width: ${cseSwatchDeprecated.sizeLarge};
    height: ${cseSwatchDeprecated.sizeLarge};
  }

  .variant-text.size-small {
    min-width: ${cseSwatchDeprecated.sizeSmall};
    width: fit-content;
    padding-inline: ${cseTextSwatch.paddingInlineSmall};
  }

  .variant-text.size-large {
    min-width: ${cseSwatchDeprecated.sizeLarge};
    width: fit-content;
    padding-inline: ${cseTextSwatch.paddingInlineLarge};
  }

  .variant-color.size-small {
    width: ${cseColorSwatchDeprecated.sizeSmall};
    height: ${cseColorSwatchDeprecated.sizeSmall};
  }

  .variant-color.size-large {
    width: ${cseColorSwatchDeprecated.sizeLarge};
    height: ${cseColorSwatchDeprecated.sizeLarge};
  }
`
